import React from "react";

import type * as types from "types";

/** Active XHR + refs. */
export interface ActiveStream {
  xhr: XMLHttpRequest;
  lastRequestedUserMessageId: string;
  lastRequestedUserMessage: types.Message;
  /**
   * Set by `abort()` so the readystate handler can distinguish a
   * user-initiated stop (silent) from a real network failure (logged).
   * Read after `xhr.onabort` deletes this entry, so the flag is captured
   * via the entry reference rather than a fresh lookup.
   */
  userAborted: boolean;
}

/**
 * The chat requests `useChatStream` has in flight, keyed by thread ID. A reply
 * streams in a thread while it has an entry. Entries outlive the component
 * that started them.
 */
const activeStreams = new Map<string, ActiveStream>();

/** Called when a stream starts or ends. */
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

export function getActiveStream(threadId: string): ActiveStream | undefined {
  return activeStreams.get(threadId);
}

export function hasActiveStream(threadId: string): boolean {
  return activeStreams.has(threadId);
}

export function setActiveStream(threadId: string, activeStream: ActiveStream) {
  activeStreams.set(threadId, activeStream);
  notifyListeners();
}

/** Several end handlers can run for one stream, so a second call does nothing. */
export function deleteActiveStream(threadId: string) {
  if (!activeStreams.delete(threadId)) return;
  notifyListeners();
}

/** Whether a reply is streaming in the thread. Re-renders on start and end. */
export function useIsThreadStreaming(threadId: string | undefined): boolean {
  return React.useSyncExternalStore(
    subscribe,
    () => threadId !== undefined && activeStreams.has(threadId)
  );
}

/** Resolves once no reply is streaming in the thread, or at once if none is. */
export function waitForStreamEnd(threadId: string): Promise<void> {
  return new Promise((resolve) => {
    const resolveIfEnded = () => {
      if (activeStreams.has(threadId)) return;
      unsubscribe();
      resolve();
    };
    const unsubscribe = subscribe(resolveIfEnded);
    resolveIfEnded();
  });
}
